import BootcampCard from "@/components/Bootcamp/BootcampCard";
import { ArrowLeftIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import { useEffect, useState } from "react";
import Layout from "@/components/layout";
import { motion } from "framer-motion";
import { useRouter } from "next/router";
import { cn } from "@/utils/cn";
import { financeId } from "../masterclasses";
import { NextPageContext } from "next";
import { Tag } from "../teachers";
import webengage from "@/webengage";
import mixpanel from "@/utils/mixpanel";
import Head from "next/head";
import Breadcrumb from "@/components/BreadCrumb";

export async function getServerSideProps(ctx: NextPageContext) {
  try {
    const subCategory = ctx.query.category as string;

    const { data: fetchedSubCategories } = await axios.get(
      `${process.env.NEXT_PUBLIC_BASE_API}categories/sub-categories/${financeId}`,
      {
        headers: { "Accept-Encoding": "gzip,deflate,compress" },
      }
    );

    let fetchUrl = `${process.env.NEXT_PUBLIC_BASE_API}bootcamps?getFutureBootcamps=true&select=sessions&category=Finance`;

    if (subCategory && typeof subCategory === "string") {
      const subCategoryId = fetchedSubCategories.data.find(
        (tag: any) => tag.slug === subCategory
      )?.id;

      if (subCategoryId) {
        fetchUrl = `${process.env.NEXT_PUBLIC_BASE_API}bootcamps/subCategory/${subCategoryId}?getFutureBootcamps=true`;
      }
    }

    const { data: bootcamps } = await axios.get(fetchUrl, {
      headers: { "Accept-Encoding": "gzip,deflate,compress" },
    });

    return {
      props: { bootcamps: bootcamps?.data, tags: fetchedSubCategories?.data },
    };
  } catch (err) {
    return { props: { bootcamps: [], tags: [] } };
  }
}

export const CLASS_TYPE = {
  MASTERCLASS: "Masterclass",
  BOOTCAMP: "Bootcamp",
};

export default function Bootcamps({
  bootcamps,
  tags,
}: {
  bootcamps: any;
  tags: any;
}) {
  const router = useRouter();
  const [activeCategory, setActiveCategory] = useState<{
    name: string;
    id: string;
    slug: string;
  } | null>(null);

  const tagsArray = tags || [];
  const TAGS: Tag = {};
  tagsArray.forEach((tag: any) => {
    TAGS[tag.slug] = {
      name: tag?.name,
      id: tag?.id,
      slug: tag?.slug,
    };
  });

  useEffect(() => {
    setActiveCategory(
      router.query.category ? TAGS[router.query.category.toString()] : null
    );
  }, [router.query.category]);

  useEffect(() => {
    const _webengage = webengage();
    if (_webengage)
      _webengage.track("Bootcamps page viewed", {
        source: "Tradewise",
      });
  }, []);

  return (
    <>
      <Head>
        <title>Advance Trading Courses - By Trading Experts | Tradewise</title>
        <meta
          name="description"
          content="Enroll in advance trading courses at Tradewise. Learn cutting-edge strategies, expert insights, & personalized guidance to elevate your trading skills."
        />
      </Head>
      <Layout phone="+918048822399">
        <Breadcrumb classNames="mb-1" />
        <div className="flex flex-col gap-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ease: "linear", duration: 0.25 }}
            className="flex flex-col gap-4 md:flex-row md:justify-between"
          >
            <div className="flex flex-row gap-4 justify-start items-center">
              <ArrowLeftIcon
                className="w-4 h-4 hover:cursor-pointer md:hidden"
                onClick={() => router.back()}
              />
              <h3 className="text-[#161C2B] max-lg:text-xl font-bold">
                Bootcamps
              </h3>
            </div>
            <div className="max-md:w-full overflow-x-auto hide_scrollbar max-md:flex max-md:gap-1 max-md:justify-evenly max-md:items-center md:space-x-4">
              <button
                onClick={() => router.push("/bootcamps")}
                className={cn(
                  "rounded-full whitespace-nowrap text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none",

                  !activeCategory
                    ? "bg-foreground !text-white hover:!text-white"
                    : "bg-primary/10 text-foreground"
                )}
              >
                All
              </button>
              {tagsArray
                .filter((tag: any) => tag.name !== "Forex")
                .map((tag: any) => (
                  <button
                    onClick={() => {
                      const _webengage = webengage();
                      if (_webengage) {
                        _webengage.track("Category Clicked", {
                          industry: tag.name,
                          source: "Tradewise",
                        });
                      }
                      router.push(`/bootcamps?category=${tag.slug}`);
                      mixpanel.track("explore_category", {
                        page_location: "Explore BootCamps",
                      page_referrer: document.referrer,
                      location: "tabs",
                      subcategory: tag?.name,
                    });
                  }}
                  className={cn(
                    "rounded-full text-sm md:text-[15px] px-3 md:px-3.5 py-1 md:py-1.5 font-medium capitalize border-none whitespace-nowrap",
                    tag?.name === activeCategory?.name
                      ? "bg-foreground !text-white hover:!text-white"
                      : "bg-primary/10 text-foreground"
                  )}
                  key={tag?.name}
                >
                  {tag?.name}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="flex flex-col py-2 gap-8 md:grid lg:grid-cols-3 md:grid-cols-2 justify-center items-center">
            {bootcamps?.map((bootcamp: any, index: number) => (
              <BootcampCard
                initial={{ opacity: 0, translateY: 40 }}
                animate={{ opacity: 1, translateY: 0 }}
                transition={{
                  duration: 0.4,
                  delay: 0.2 * index,
                  type: "tween",
                }}
                viewport={{ once: true }}
                bootcamp={bootcamp}
                key={bootcamp?.id}
              />
            ))}
          </div>
        </div>
      </Layout>
    </>
  );
}
